import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './MicrositePopups.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, Plus } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeTable from '../components/MicrositeTable/MicrositeTable'
import MicrositePagination from '../components/MicrositePagination/MicrositePagination'
import MicrositeTriggerBadge from '../components/MicrositeTriggerBadge/MicrositeTriggerBadge'
import MicrositeConversionRate from '../components/MicrositeConversionRate/MicrositeConversionRate'
import ToggleSwitch from '../components/ToggleSwitch/ToggleSwitch'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'

const PAGE_LIMIT = 20

const MicrositePopups = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)
    const [rows, setRows] = useState([])
    const [cursorStack, setCursorStack] = useState([0])
    const [nextCursor, setNextCursor] = useState(null)

    const currentCursor = cursorStack[cursorStack.length - 1]

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadPopups = useCallback(async (cursor) => {
        setLoading(true)
        const res = await post('micrositeGetPopups', { limit: PAGE_LIMIT, cursor: cursor || 0 })
        if (res?.status === 'success') {
            setRows(res.response?.data ?? [])
            setNextCursor(res.response?.nextCursor ?? null)
        } else {
            showToast('Could not load popups. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadPopups(currentCursor)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, currentCursor])

    const handleToggleActive = async (row, isActive) => {
        setRows((prev) => prev.map((r) => (r.ID === row.ID ? { ...r, isActive } : r)))
        const res = await post('micrositeUpdatePopup', { ID: row.ID, ...row, isActive })
        if (res?.status !== 'success') {
            showToast('Could not update popup status', 'error')
            setRows((prev) => prev.map((r) => (r.ID === row.ID ? { ...r, isActive: !isActive } : r)))
        }
    }

    const handleDelete = async (ID) => {
        const res = await post('micrositeDeletePopup', { ID })
        if (res?.status === 'success') {
            setRows((prev) => prev.filter((r) => r.ID !== ID))
            showToast('Popup deleted')
        } else {
            showToast('Could not delete popup', 'error')
        }
    }

    const columns = [
        {
            key: 'popupName',
            label: 'Popup',
            render: (row) => <span className={styles.name}>{row.popupName}</span>
        },
        {
            key: 'triggerType',
            label: 'Trigger',
            render: (row) => <MicrositeTriggerBadge value={row.triggerType} triggerValue={row.triggerValue} />
        },
        {
            key: 'conversion',
            label: 'Performance',
            render: (row) => (
                <MicrositeConversionRate impressions={row.impressionCount ?? 0} conversions={row.conversionCount ?? 0} />
            )
        },
        {
            key: 'isActive',
            label: 'Active',
            render: (row) => (
                <ToggleSwitch size="sm" checked={!!row.isActive} onChange={(v) => handleToggleActive(row, v)} />
            )
        },
        {
            key: 'actions',
            label: '',
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <button
                        type="button"
                        className={styles.editLink}
                        onClick={() => navigate(`/microsite/popups/${row.ID}/edit`)}
                    >
                        Edit
                    </button>
                    <MicrositeDeleteConfirm mode="inline" onConfirm={() => handleDelete(row.ID)} />
                </div>
            )
        }
    ]

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Popups"
                subtitle="Promotional popups shown to visitors based on triggers like exit intent or scroll depth."
                primaryAction={{ label: 'New popup', icon: <Plus size={14} />, onClick: () => navigate('/microsite/popups/new') }}
            />

            <MicrositeTable
                columns={columns}
                rows={rows}
                getRowKey={(row) => row.ID}
                loading={loading}
                emptyMessage="No popups yet"
                emptyDescription="Create one to promote offers or capture leads"
            />

            <MicrositePagination
                hasPrev={cursorStack.length > 1}
                hasNext={!!nextCursor}
                onPrev={() => setCursorStack((s) => s.slice(0, -1))}
                onNext={() => setCursorStack((s) => [...s, nextCursor])}
                loading={loading}
                currentCount={rows.length}
            />
        </div>
    )
}

export default MicrositePopups